/**
 * autoGrade.js — settle pending tracker records against final scores
 * from the site's existing scores pipeline (services/oddsApi
 * fetchScoresForSport, which reads the odds-api proxy's scores
 * endpoint). The fetch function is injected so this module is testable
 * in plain node with a stub.
 *
 * Semantics come from the ported prototype (ingest/grading.js) and are
 * NOT loosened here:
 *   - moneyline / spread / total legs grade from final scores;
 *   - player-prop legs (builder Phase 2) grade from the player's
 *     actual stat line for that game (player-hit-rate game lookup
 *     over the playerStats store) — the one deliberate extension,
 *     held to the same never-guess standard;
 *   - a parlay settles won only when every leg wins, lost when any
 *     leg loses;
 *   - a parlay with a PUSH leg is left pending (books reduce the
 *     ticket around the push and reprice it — we never guess that
 *     payout; the user settles it with the manual override);
 *   - anything unmatchable or ambiguous is left pending. A bet is
 *     NEVER auto-graded lost (or won) on missing data.
 *
 * Matching: builder-saved legs carry the Odds API eventId — when a
 * scores event matches a leg's eventId, that pairing wins outright.
 * Otherwise the prototype's team/event-string matching applies, with
 * candidate results ordered by closeness to the leg's commenceTime so
 * repeat matchups inside the scores window resolve to the right game.
 */

import { gradeBet } from './ingest/grading.js';
import { toModelBet, sportKeyForSport, settlementUpdates } from './records.js';

const ANYTIME_TD_MARKET = 'player_anytime_td';

/**
 * Grade one prop leg against the player's actual stat value.
 * Mirrors the server's utils/playerGame gradePropValue exactly:
 * over/under vs the line (exact landing pushes); anytime TD is
 * yes/no (value > 0 wins). Returns 'won' | 'lost' | 'push' | null
 * (null = can't grade honestly from this value).
 */
export function gradePropLegValue(leg, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (leg.market === ANYTIME_TD_MARKET) return value > 0 ? 'won' : 'lost';
  const line = typeof leg.line === 'number' ? leg.line
    : (typeof leg.point === 'number' ? leg.point : null);
  if (line === null) return null;
  if (value === line) return 'push';
  const over = value > line;
  if (leg.side === 'over') return over ? 'won' : 'lost';
  if (leg.side === 'under') return over ? 'lost' : 'won';
  return null;
}

/** Signed decision margin for a graded prop leg (bettor's view). */
export function marginForPropLeg(leg, value) {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  if (leg.market === ANYTIME_TD_MARKET) return value > 0 ? 1 : -1;
  const line = typeof leg.line === 'number' ? leg.line
    : (typeof leg.point === 'number' ? leg.point : null);
  if (line === null) return null;
  return leg.side === 'under' ? line - value : value - line;
}

/**
 * Default player-game lookup: the player-hit-rate function's
 * game=1 mode, which identifies the player's game in the
 * playerStats store and returns { value, result }. Any failure
 * resolves null — the leg stays pending, never guessed.
 */
async function defaultFetchPlayerGame(leg) {
  try {
    const params = new URLSearchParams({
      game: '1',
      player: leg.player || '',
      league: leg.league || '',
      market: leg.market || '',
      side: leg.side || '',
      commenceTime: leg.commenceTime || '',
      homeTeam: leg.homeTeam || '',
      awayTeam: leg.awayTeam || ''
    });
    const line = typeof leg.line === 'number' ? leg.line
      : (typeof leg.point === 'number' ? leg.point : null);
    if (line !== null) params.set('line', String(line));
    const base = window.location.hostname === 'localhost'
      ? 'http://localhost:8888/.netlify/functions/player-hit-rate'
      : '/.netlify/functions/player-hit-rate';
    const response = await fetch(`${base}?${params.toString()}`);
    if (!response.ok) return null;
    return await response.json();
  } catch (err) {
    return null;
  }
}

/** Odds API scores events -> grading.js result objects. */
export function scoresToResults(events) {
  const results = [];
  for (const event of events || []) {
    if (!event || event.completed !== true || !Array.isArray(event.scores)) continue;
    const home = event.scores.find((s) => s.name === event.home_team);
    const away = event.scores.find((s) => s.name === event.away_team);
    if (!home || !away) continue;
    const homeScore = Number(home.score);
    const awayScore = Number(away.score);
    if (!Number.isFinite(homeScore) || !Number.isFinite(awayScore)) continue;
    results.push({
      id: event.id,
      event: `${event.away_team} at ${event.home_team}`,
      homeTeam: event.home_team,
      awayTeam: event.away_team,
      homeScore,
      awayScore,
      final: true,
      commenceTime: event.commence_time
    });
  }
  return results;
}

/**
 * Grade every pending record. Returns [{ id, updates }] — Firestore
 * update field sets for the records that settled; the caller persists
 * them via betTrackerService.updateBet. Records that can't be settled
 * with certainty simply don't appear in the list.
 *
 * fetchScores: (sportKey, daysFrom) => Promise<Odds API scores events>
 * (oddsApi.fetchScoresForSport in the app; a stub in tests).
 *
 * Records containing player-prop legs (Parlay Builder Phase 2)
 * take a dedicated path: game legs grade from the same scores as
 * usual, prop legs grade from the player's actual stat line via
 * fetchPlayerGame (the player-hit-rate game lookup; a stub in
 * tests). A mixed record settles only when EVERY leg resolves —
 * and, per the standing tracker rule above, a push anywhere in
 * the slip leaves it pending for the manual override.
 */
export async function autoGradeRecords(records, fetchScores, settledAt = new Date().toISOString(), fetchPlayerGame = defaultFetchPlayerGame) {
  const pending = (records || []).filter((r) => r.status === 'pending');
  if (pending.length === 0) return [];

  const sportKeys = new Set();
  for (const record of pending) {
    for (const leg of record.legs || []) {
      const key = sportKeyForSport(leg.sport);
      if (key) sportKeys.add(key);
    }
  }

  const resultsByKey = {};
  await Promise.all([...sportKeys].map(async (key) => {
    try {
      resultsByKey[key] = scoresToResults(await fetchScores(key, 3));
    } catch (err) {
      // A failed scores fetch leaves that sport's bets pending — it
      // must never turn into a guessed grade.
      resultsByKey[key] = [];
    }
  }));

  const updates = [];
  for (const record of pending) {
    const hasPropLegs = (record.legs || []).some((leg) => leg.type === 'prop');
    const seen = new Set();
    let results = [];
    for (const leg of record.legs || []) {
      const key = sportKeyForSport(leg.sport);
      for (const result of (key && resultsByKey[key]) || []) {
        const dedupeKey = result.id || result.event;
        if (seen.has(dedupeKey)) continue;
        seen.add(dedupeKey);
        results.push(result);
      }
    }
    // All-prop records can settle without any team scores at all.
    if (results.length === 0 && !hasPropLegs) continue;

    // eventId pairings win: relabel the matched scores event with the
    // record's stored team names so the prototype's exact event match
    // finds it even if the feed's naming drifted.
    results = results.map((result) => {
      const leg = (record.legs || []).find((l) => l.eventId && l.eventId === result.id);
      if (!leg) return result;
      return {
        ...result,
        event: `${leg.awayTeam} at ${leg.homeTeam}`,
        homeTeam: leg.homeTeam,
        awayTeam: leg.awayTeam
      };
    });

    // Closest game first: findResultForBet takes the first match, so
    // ordering by distance to the first leg's start resolves repeat
    // matchups (same teams twice in the window) to the right game.
    const t0 = Date.parse((record.legs[0] || {}).commenceTime || record.createdAt) || 0;
    results.sort((a, b) =>
      Math.abs((Date.parse(a.commenceTime) || 0) - t0) -
      Math.abs((Date.parse(b.commenceTime) || 0) - t0));

    if (hasPropLegs) {
      // Dedicated mixed-slip path (see the header note): every leg
      // must resolve, and any push leaves the record pending.
      const legGrades = [];
      let unresolved = false;
      let sawPush = false;
      let sawLoss = false;
      for (const leg of record.legs || []) {
        if (leg.type === 'prop') {
          let playerGame = null;
          try {
            playerGame = await fetchPlayerGame(leg);
          } catch (err) {
            playerGame = null;
          }
          const outcome = playerGame ? gradePropLegValue(leg, playerGame.value) : null;
          if (!outcome) { unresolved = true; break; }
          if (outcome === 'push') sawPush = true;
          if (outcome === 'lost') sawLoss = true;
          legGrades.push({ outcome, margin: marginForPropLeg(leg, playerGame.value) });
          continue;
        }
        const byEvent = leg.eventId ? results.find((r) => r.id === leg.eventId) : null;
        const single = toModelBet({ ...record, type: 'single', legs: [leg] });
        const legGrade = gradeBet(single, byEvent || results);
        if (!legGrade || legGrade.outcome === 'needs_review') { unresolved = true; break; }
        if (legGrade.outcome === 'push') sawPush = true;
        if (legGrade.outcome === 'lost') sawLoss = true;
        legGrades.push({
          outcome: legGrade.outcome,
          margin: typeof legGrade.margin === 'number' ? legGrade.margin : null
        });
      }
      if (unresolved || sawPush) continue;
      const outcome = sawLoss ? 'lost' : 'won';
      updates.push({
        id: record.id,
        updates: settlementUpdates(record, outcome, {
          margin: null,
          legMargins: legGrades.map((g) => g.margin),
          settledAt,
          settledBy: 'auto'
        })
      });
      continue;
    }

    const grade = gradeBet(toModelBet(record), results);
    if (!grade || grade.outcome === 'needs_review') continue;

    const legMargins = grade.legs
      ? grade.legs.map((leg) => (typeof leg.margin === 'number' ? leg.margin : null))
      : (record.legs || []).map(() => (typeof grade.margin === 'number' ? grade.margin : null));

    updates.push({
      id: record.id,
      updates: settlementUpdates(record, grade.outcome, {
        margin: typeof grade.margin === 'number' ? grade.margin : null,
        legMargins,
        settledAt,
        settledBy: 'auto'
      })
    });
  }
  return updates;
}
